/**
 * Vendor verification.
 *
 * SEARCH, not a list. There is no list-all endpoint: `findVendorsSchema`
 * requires at least two characters, so the shared vendor table cannot be walked
 * one letter at a time. The screen is shaped by that, not by preference.
 *
 * The verified flag is set only here
 * (`PATCH /admin/vendors/:id/verified`, `admin:vendors:verify`). Creating and
 * reading vendors belongs to designers and contractors sourcing materials, on
 * `/api/v1/vendors`. This page searches and creates through
 * `/api/v1/admin/vendors`, which runs the same handlers and schemas: the
 * console holds only the admin session, which `/api/v1/vendors` does not read
 * (#457). Those routes are gated on `admin:vendors:verify` too, the
 * permission this page already requires.
 */

import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Skeleton } from "@shared-ui/skeleton";
import { Switch } from "@shared-ui/switch";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { DataTable } from "@/features/catalog/components/data-table";
import { useDebounce } from "@/lib/use-debounce";
import { AddVendorDialog } from "../components/add-vendor-dialog";
import {
	findVendors,
	setVendorVerified,
} from "../services/vendors-api.service";

const MIN_QUERY_LENGTH = 2;

export function VendorsDashboardPage() {
	const queryClient = useQueryClient();
	const [query, setQuery] = useState("");
	const [isAddingVendor, setIsAddingVendor] = useState(false);
	const debouncedQuery = useDebounce(query, 300);
	const canSearch = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;

	const vendorsQuery = useQuery({
		queryKey: ["admin-vendors", debouncedQuery],
		queryFn: () => findVendors(debouncedQuery.trim()),
		enabled: canSearch,
		placeholderData: keepPreviousData,
	});

	const verifyMutation = useMutation({
		mutationFn: (input: { vendorId: string; isVerified: boolean }) =>
			setVendorVerified(input.vendorId, input.isVerified),
		onSuccess: async (vendor) => {
			await queryClient.invalidateQueries({ queryKey: ["admin-vendors"] });
			toast.success(
				vendor.isVerified
					? `${vendor.name} is now verified`
					: `${vendor.name} is no longer verified`,
			);
		},
	});

	const vendors = vendorsQuery.data ?? [];

	return (
		<div className="grid gap-5">
			<div className="flex items-start justify-between gap-4">
				<div className="grid gap-2">
					<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
						Supply
					</p>
					<h2 className="text-2xl font-semibold tracking-tight">Vendors</h2>
					<p className="text-sm text-muted-foreground">
						The verified badge is visible to every designer on the platform.
						Granting it is the one vendor action reserved to admins.
					</p>
				</div>
				<Button onClick={() => setIsAddingVendor(true)}>Add vendor</Button>
			</div>

			{isAddingVendor ? (
				<AddVendorDialog
					onClose={() => setIsAddingVendor(false)}
					onCreated={async (vendor, nearMatches) => {
						setIsAddingVendor(false);
						await queryClient.invalidateQueries({
							queryKey: ["admin-vendors"],
						});
						toast.success(`${vendor.name} added`);
						if (nearMatches.length > 0) {
							toast.warning(
								`Similar vendor${nearMatches.length > 1 ? "s" : ""} already exist: ${nearMatches
									.map((match) => match.name)
									.join(", ")}`,
							);
						}
					}}
				/>
			) : null}

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Find a vendor</CardTitle>
					<CardDescription>
						Type at least {MIN_QUERY_LENGTH} characters. There is no full
						listing — search for a vendor before adding a new one, so you don't
						create a duplicate.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<Input
						placeholder="Vendor name"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>

					{!canSearch ? (
						<EmptyState
							title={`Type at least ${MIN_QUERY_LENGTH} characters to search.`}
						/>
					) : vendorsQuery.isLoading ? (
						<Skeleton className="h-48 w-full rounded-2xl" />
					) : vendors.length === 0 ? (
						<EmptyState title="No vendors match that search." />
					) : (
						<DataTable
							rows={vendors}
							rowKey={(vendor) => vendor.id}
							isRefreshing={vendorsQuery.isFetching}
							columns={[
								{
									header: "Vendor",
									cell: (vendor) => vendor.name,
									cellClassName: "font-medium",
								},
								{
									header: "Location",
									cell: (vendor) =>
										[vendor.city, vendor.state].filter(Boolean).join(", ") ||
										"—",
									cellClassName: "text-muted-foreground",
								},
								{
									header: "Status",
									cell: (vendor) =>
										vendor.isVerified ? (
											<Badge variant="success">Verified</Badge>
										) : (
											<Badge variant="default">Unverified</Badge>
										),
								},
								{
									header: "Verified",
									cell: (vendor) => (
										<Switch
											aria-label={`Verified: ${vendor.name}`}
											checked={vendor.isVerified}
											disabled={verifyMutation.isPending}
											onCheckedChange={(checked) =>
												verifyMutation.mutate({
													vendorId: vendor.id,
													isVerified: checked,
												})
											}
										/>
									),
								},
							]}
						/>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
